/* eslint-disable ember/no-computed-properties-in-native-classes */
import Model, {
  attr,
  belongsTo,
  type AsyncBelongsTo,
} from '@warp-drive/legacy/model';
import { computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import type { Type } from '@warp-drive/core/types/symbols';
import type { StatefulArray } from 'ember-data-model-fragments/array/stateful';
import { array } from 'ember-data-model-fragments/attributes';
import type { EnumValue } from '@linc-technologies/linced-common/enums/base-enum';
import GradingMethod from '@linc-technologies/linced-common/enums/grading-method';
import type PersonType from '@linc-technologies/linced-common/enums/person-type';
import type Assessment from '@linc-technologies/linced-common/models/assessments/assessment';
import type Module from '@linc-technologies/linced-common/models/assessments/module';
import type Person from '@linc-technologies/linced-common/models/person';
import type School from '@linc-technologies/linced-common/models/school';
import moment from 'moment';

// Proto: assessments.Grade.
export default class Grade extends Model {
  declare [Type]: 'assessments/grade';

  @belongsTo('school', { async: true, inverse: null })
  declare schoolId: AsyncBelongsTo<School>;

  @alias('schoolId')
  declare school: AsyncBelongsTo<School>;

  @attr('string', { defaultValue: '' })
  declare campusId: string; // TODO: implement when we have Campuses

  @attr('number')
  declare updateTime: number | undefined;

  @attr('number')
  declare createTime: number | undefined;

  @computed('createTime')
  get date() {
    return this.createTime
      ? moment.unix(this.createTime).format('YYYY-MM-DD')
      : '';
  }

  @computed('createTime')
  get year() {
    return this.createTime ? moment.unix(this.createTime).format('YYYY') : '';
  }

  @belongsTo('assessments/assessment', { async: true, inverse: null })
  declare assessmentId: AsyncBelongsTo<Assessment>;

  @alias('assessmentId')
  declare assessment: AsyncBelongsTo<Assessment>;

  @alias('assessment.id')
  declare assessment_id: string | null | undefined;

  @belongsTo('assessments/module', { async: true, inverse: null })
  declare assessmentModuleId: AsyncBelongsTo<Module>;

  @alias('assessmentModuleId')
  declare module: AsyncBelongsTo<Module>;

  @alias('module.id')
  declare module_id: string | null | undefined;

  @belongsTo('person', { async: true, inverse: null })
  declare assessorId: AsyncBelongsTo<Person>;

  @alias('assessorId')
  declare assessor: AsyncBelongsTo<Person>;

  @belongsTo('person', { async: true, inverse: null })
  declare personId: AsyncBelongsTo<Person>;

  @alias('personId')
  declare person: AsyncBelongsTo<Person>;

  @alias('person.id')
  declare person_id: string | null | undefined;

  @attr('number', { defaultValue: 0 })
  declare gradeType: number;

  @attr('number', { defaultValue: 0 })
  declare status: number;

  @attr('string', { defaultValue: '' })
  declare reason: string;

  @attr('string', { defaultValue: '' })
  declare selectAwarded: string;

  @attr('string', { defaultValue: '' })
  declare iconAwarded: string;

  @array('string')
  declare multiSelectAwarded: StatefulArray<string>;

  @attr('number', { defaultValue: 0 })
  declare numericAwarded: number;

  @attr('number', { defaultValue: 0 })
  declare numericScale: number;

  @computed(
    'gradeType',
    'selectAwarded',
    'multiSelectAwarded',
    'numericAwarded',
    'numericScale',
    'iconAwarded',
  )
  get awarded() {
    switch (this.gradeType) {
      case GradingMethod.SELECT:
        return this.selectAwarded;
      case GradingMethod.MULTIPLE_SELECT:
        return this.multiSelectAwarded.toArray().join(', ');
      case GradingMethod.NUMERIC:
        return this.numericAwarded * Math.pow(10, this.numericScale);
      case GradingMethod.ICON_SELECT:
        return this.iconAwarded;
    }
  }

  @attr('string')
  declare auditReason: string | undefined; // This doesn't exist on the back end.  It's used for UPDATE and DELETE requests.

  @attr('number', { defaultValue: 0 })
  declare assessorType: EnumValue<typeof PersonType> | 0;
}
